/**
 * Marketing attribution capture & persistence.
 *
 * Captures `utm_*` parameters plus paid click identifiers (`gclid`, `fbclid`)
 * from the URL on first load, persists them in `sessionStorage` (fast in-tab
 * lookup) and a 90-day cookie (cross-tab/restart persistence), and exposes
 * helpers to attach the captured context to conversion events and form
 * submissions.
 *
 * Strategy: **last-touch when present, otherwise persist.**
 *  - If the current URL contains any tracking parameter, replace the stored
 *    attribution (the freshest paid click wins).
 *  - If the URL has no tracking parameters, preserve whatever is already
 *    stored — direct or internal navigation must not clobber a prior paid
 *    click within the 90-day window.
 *
 * Without this, the URL-borne campaign metadata is gone the moment the user
 * navigates and ad attribution becomes guesswork.
 */

const COOKIE_NAME = 'cwnc_attr'
const SESSION_KEY = 'cwnc_attr'
const COOKIE_MAX_AGE_DAYS = 90

const TRACKING_PARAMS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'gclid',
  'fbclid',
] as const

type TrackingParam = (typeof TRACKING_PARAMS)[number]

export interface AttributionContext {
  utm_source?: string
  utm_medium?: string
  utm_campaign?: string
  utm_content?: string
  utm_term?: string
  /** Google Ads click ID. */
  gclid?: string
  /** Meta Ads click ID — required for high-quality CAPI matching. */
  fbclid?: string
  /** `document.referrer` at first capture. */
  referrer?: string
  /** First URL pathname the user landed on. */
  landing_path?: string
  /** ISO timestamp of capture. */
  captured_at?: string
}

let cached: AttributionContext | null = null

// ---- storage helpers ----

function readCookie(): AttributionContext | null {
  if (typeof document === 'undefined') return null
  const match = document.cookie.match(new RegExp(`(?:^|; )${COOKIE_NAME}=([^;]*)`))
  if (!match) return null
  try {
    return JSON.parse(decodeURIComponent(match[1])) as AttributionContext
  } catch {
    return null
  }
}

function writeCookie(value: AttributionContext): void {
  if (typeof document === 'undefined') return
  const encoded = encodeURIComponent(JSON.stringify(value))
  const maxAge = COOKIE_MAX_AGE_DAYS * 24 * 60 * 60
  document.cookie = `${COOKIE_NAME}=${encoded}; path=/; max-age=${maxAge}; SameSite=Lax`
}

function readSession(): AttributionContext | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.sessionStorage.getItem(SESSION_KEY)
    return raw ? (JSON.parse(raw) as AttributionContext) : null
  } catch {
    return null
  }
}

function writeSession(value: AttributionContext): void {
  if (typeof window === 'undefined') return
  try {
    window.sessionStorage.setItem(SESSION_KEY, JSON.stringify(value))
  } catch {
    // sessionStorage may be unavailable in private mode — silent fail
  }
}

// ---- URL parsing ----

function readTrackingFromUrl(): Partial<AttributionContext> {
  if (typeof window === 'undefined') return {}
  const params = new URLSearchParams(window.location.search)
  const out: Partial<AttributionContext> = {}
  for (const key of TRACKING_PARAMS) {
    const v = params.get(key)
    if (v) out[key as TrackingParam] = v
  }
  return out
}

function hasTrackingParams(ctx: Partial<AttributionContext>): boolean {
  return TRACKING_PARAMS.some((k) => Boolean(ctx[k]))
}

// ---- public API ----

/**
 * Capture attribution from the current URL on app init.
 *
 * Safe to call once per page load. Returns the resolved attribution context
 * (URL params if present, otherwise the previously stored value, otherwise
 * a soft-attribution capture of just `referrer` + `landing_path`).
 */
export function captureAttribution(): AttributionContext {
  const fromUrl = readTrackingFromUrl()
  const stored = readCookie() ?? readSession() ?? null

  if (hasTrackingParams(fromUrl)) {
    const captured: AttributionContext = {
      ...fromUrl,
      referrer:
        typeof document !== 'undefined' ? document.referrer || undefined : undefined,
      landing_path:
        typeof window !== 'undefined' ? window.location.pathname : undefined,
      captured_at: new Date().toISOString(),
    }
    writeCookie(captured)
    writeSession(captured)
    cached = captured
    return captured
  }

  if (stored) {
    cached = stored
    // Refresh sessionStorage so the in-tab fast path is warm even if only the
    // cookie survived a restart.
    writeSession(stored)
    return stored
  }

  // No URL tracking and no stored attribution — record soft attribution
  // (referrer + landing path only) so we still have something to report.
  const soft: AttributionContext = {
    referrer:
      typeof document !== 'undefined' ? document.referrer || undefined : undefined,
    landing_path:
      typeof window !== 'undefined' ? window.location.pathname : undefined,
    captured_at: new Date().toISOString(),
  }
  if (soft.referrer || soft.landing_path) {
    writeSession(soft)
  }
  cached = soft
  return soft
}

/**
 * Read the current attribution context. Falls back to session/cookie storage
 * if `captureAttribution()` has not been called yet in this tab.
 */
export function getAttributionContext(): AttributionContext {
  if (cached) return cached
  const stored = readSession() ?? readCookie() ?? {}
  cached = stored
  return stored
}

/**
 * Flatten the attribution context into a `Record<string, string>` suitable
 * for merging into a Firebase event payload, a Cal.com booking metadata
 * field, or a form submission body. Only defined values are included.
 */
export function attributionToPayload(): Record<string, string> {
  const ctx = getAttributionContext()
  const out: Record<string, string> = {}
  for (const [k, v] of Object.entries(ctx)) {
    if (v) out[k] = String(v)
  }
  return out
}

/**
 * Test helper — clears stored attribution from cookie + sessionStorage.
 * Only available in test/development environments for safety.
 */
export function __clearAttribution(): void {
  if (import.meta.env?.MODE !== 'test' && import.meta.env?.MODE !== 'development') {
    console.warn('__clearAttribution() is only available in test/development mode')
    return
  }
  cached = null
  if (typeof document !== 'undefined') {
    document.cookie = `${COOKIE_NAME}=; path=/; max-age=0; SameSite=Lax`
  }
  if (typeof window !== 'undefined') {
    try {
      window.sessionStorage.removeItem(SESSION_KEY)
    } catch {
      // ignore
    }
  }
}
